/**
 * Stable identifier for an error the client has to react to — translate it,
 * redirect on it. The `message` stays English for logs and Sentry; the code is
 * what crosses the GraphQL boundary (as `extensions.code`, see the API's
 * `formatError`) and what the client keys off. Never match on the message.
 */
export enum ErrorCode {
  InvalidTenant = "INVALID_TENANT",
  TimereportAlreadySentToCustomer = "TIMEREPORT_ALREADY_SENT_TO_CUSTOMER",
  TimereportNotSendableStatus = "TIMEREPORT_NOT_SENDABLE_STATUS",
  TimereportNotEditable = "TIMEREPORT_NOT_EDITABLE",
  TimereportEmailSendFailed = "TIMEREPORT_EMAIL_SEND_FAILED",
  TimereportEmailPartiallySent = "TIMEREPORT_EMAIL_PARTIALLY_SENT",
  TimereportNoCustomerContact = "TIMEREPORT_NO_CUSTOMER_CONTACT",
  PdfPreviewUnavailable = "PDF_PREVIEW_UNAVAILABLE",
  FreshdeskTicketCloseFailed = "FRESHDESK_TICKET_CLOSE_FAILED",
  SmsTextInvalid = "SMS_TEXT_INVALID"
}

// this error doesn't get logged in AI as exception
export abstract class NonCriticalError extends Error {
  readonly code?: ErrorCode;
  /**
   * What the client needs to word the error itself, e.g. which characters an
   * SMS may not contain. Sent as `extensions.details` alongside the code.
   */
  readonly details?: Record<string, unknown>;

  constructor(message?: string, code?: ErrorCode, details?: Record<string, unknown>) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

export class AuthError extends NonCriticalError {}

export class InsufficientPermissionError extends NonCriticalError {
  constructor(userEmail: string, resource: string, action: string) {
    super(`Insufficient permission: ${userEmail} cannot access ${resource}:${action}`);
  }
}

export class InvalidInputError extends NonCriticalError {}

export class InvalidSchemaValidationError extends NonCriticalError {}

export class InvalidVersionError extends NonCriticalError {}

export class InvalidUserInputError extends NonCriticalError {}

export class NotFoundError extends NonCriticalError {}

/**
 * An email did not reach (all of) its recipients. Carries a code so the client
 * can say exactly that instead of falling back to "Okänt fel"; the underlying
 * cause is logged where the send was attempted (CRMDEV-7141).
 */
export class EmailDeliveryError extends NonCriticalError {}

/**
 * A quality status was requested without the document it requires — a
 * precondition on user input, and the sibling of the `InvalidUserInputError`
 * thrown for a disabled item in the same guard (CRMDEV-7157).
 */
export class MissingDocumentError extends NonCriticalError {}

/**
 * The document exists but its PDF rendition cannot be produced — a previous
 * conversion failed deterministically and was recorded on the document
 * (CRMDEV-7169). Thrown instead of re-running the conversion, so the caller
 * fails in milliseconds rather than hanging for Gotenberg's 30 s deadline. Not
 * a `NotFoundError`: the file is there, only the preview is unavailable, and
 * retrying will not change that.
 */
export class PdfPreviewUnavailableError extends NonCriticalError {
  constructor(message = "PDF preview unavailable") {
    super(message, ErrorCode.PdfPreviewUnavailable);
  }
}

/**
 * A Freshdesk ticket the CRM tried to close automatically stayed open. Carries a
 * code so the client can say so instead of falling back to "Okänt fel"; the
 * Freshdesk status + body is logged where the close was attempted. Before
 * CRMDEV-7163 these failures were logged only, so the mutation returned 200 and
 * the user saw a success while the ticket stayed open.
 */
export class FreshdeskTicketCloseError extends NonCriticalError {}

export class InvalidTenant extends NonCriticalError {}

export class TextKernelError extends NonCriticalError {}

export class NetworkError extends NonCriticalError {
  constructor(status: number, statusText: string) {
    super(`Network error: ${status} ${statusText}`);
  }
}
